import { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Button } from "@etframework/ui.components";
import { useCreateTaskType } from "../../data/mutations/taskTypeMutations";
import { useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import { toast } from "react-toastify";
import { TaskTypeNameTextBox } from "../../components/TaskTypeComponents/CreateTaskTypeModalComponents/TaskTypeNameTextbox";
import { TaskTypeKeyTextBox } from "../../components/TaskTypeComponents/CreateTaskTypeModalComponents/TaskTypeKeyTextbox";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { ValidationProvider, useValidation } from "@etframework/ui.components";
import { AutoRolloverContent } from "../../components/PreferencesComponents/SystemPreferencesComponents/AutoRolloverContent";

export const CreateTaskTypeModal = () => {
  return (
    <ValidationProvider>
      <InnerCreateTaskTypeModal />
    </ValidationProvider>
  );
};

const InnerCreateTaskTypeModal = () => {
  const [taskTypeName, setTaskTypeName] = useState("");
  const [taskTypeKey, setTaskTypeKey] = useState("");
  const taskTypeNameRef = useRef(null);
  const taskTypeKeyRef = useRef(null);
  const autoRolloverRef = useRef(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: createTaskType, isPending } = useCreateTaskType();
  const { boardId } = useParams();
  const { setValidationErrors, clearValidationErrors } = useValidation();
  const { t } = useTranslation(["TaskTypes"], {
    useSuspense: false,
  });

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const onModelBeforeClose = () => {};

  const closeModal = () => {
    clearValidationErrors();
    taskTypeNameRef.current.setShowError(false);
    taskTypeKeyRef.current.setShowError(false);
    setShowModal(false);
  };

  const openModal = () => {
    setShowModal(true);
    setTaskTypeKey("");
    setTaskTypeName("");
  };

  const checkRequiredData = () => {
    const isNameValid = taskTypeName != "";
    const isKeyValid = taskTypeKey != "";

    const isValid = isNameValid && isKeyValid;

    if (!isNameValid)
      taskTypeNameRef.current.setError(t("CreateTaskTypeModal.Required"));
    if (!isKeyValid)
      taskTypeKeyRef.current.setError(t("CreateTaskTypeModal.Required"));

    taskTypeNameRef.current.setShowError(!isNameValid);
    taskTypeKeyRef.current.setShowError(!isKeyValid);

    return isValid;
  };

  const onCreateTaskType = () => {
    if (checkRequiredData()) {
      const nameValue = taskTypeNameRef.current.getValue();
      const keyValue = taskTypeKeyRef.current.getValue();
      const autoRolloverValue = autoRolloverRef.current?.getValue();

      const postData = {
        BoardId: boardId,
        Title: nameValue,
        Key: keyValue,
        AutoRollover: autoRolloverValue,
      };

      createTaskType(
        { taskType: postData },
        {
          onSuccess: (response) => {
            queryClient.invalidateQueries(["get-task-types", boardId]);
            closeModal();
            toast.success(t("CreateTaskTypeModal.Success"), {
              position: "bottom-left",
            });
            navigate(
              `/duedates/settings/boards/${boardId}/tasktypes/${response.Id}`
            );
          },
          onError: (errors) => {
            setValidationErrors(errors?.data);
            toast.error(t("CreateTaskTypeModal.ErrorMsg"), {
              position: "bottom-left",
            });
          },
        }
      );
    }
  };

  const ModalContent = (
    <div className="et-create-task-type-modal">
      <div>
        <span className="create-task-type-header d-block">
          {t("CreateTaskTypeModal.NewTaskType")}
        </span>
      </div>

      <div className="row">
        <div className="col">
          <TaskTypeNameTextBox
            id={ModalEvents.CreateTaskTypeModal.Id + "_title-input"}
            ref={taskTypeNameRef}
            name="Title"
            value={taskTypeName}
            change={(e) => {
              setTaskTypeName(e.value);
              clearValidationErrors();
            }}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <TaskTypeKeyTextBox
            id={ModalEvents.CreateTaskTypeModal.Id + "_key-input"}
            ref={taskTypeKeyRef}
            name="Key"
            taskTypeName={taskTypeName}
            value={taskTypeKey}
            change={(e) => {
              setTaskTypeKey(e.value);
              clearValidationErrors();
            }}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <AutoRolloverContent
            ref={autoRolloverRef}
            show={true}
            tooltipMessage={t("CreateTaskTypeModal.AutoRolloverTooltip")}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end create-task-type-modal-btns mx-auto gap-3">
        <Button
          text={t("CreateTaskTypeModal.Cancel")}
          className="btn-tertiary"
          onClick={closeModal}
        />
        <Button
          text={t("CreateTaskTypeModal.AddTaskType")}
          className="btn btn-primary"
          onClick={onCreateTaskType}
          loading={isPending}
        />
      </div>
    </div>
  );

  useRegisterEvent(ModalEvents.CreateTaskTypeModal, openModal);

  return ReactDOM.createPortal(
    <Modal
      id={ModalEvents.CreateTaskTypeModal.Id}
      ref={modalRef}
      isModal
      showCloseIcon={true}
      visible={showModal}
      content={ModalContent}
      close={closeModal}
      beforeClose={onModelBeforeClose}
      minHeight="20vh"
      width="500px"
    ></Modal>,
    document.body
  );
};
